import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faScaleBalanced, faStar, faArrowRightArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const StatCard = ({ label, value, sub, icon, color, bg, border }) => (
    <div
        style={{
            flex: '1 1 180px', minWidth: 170, maxWidth: 280,
            backgroundColor: '#fff',
            border: `1px solid ${border}`,
            borderRadius: 14, padding: '16px 18px',
            display: 'flex', flexDirection: 'column', gap: 8,
            transition: 'box-shadow 0.15s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 4px 16px ${border}bb`)}
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
    >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {label}
            </span>
            <div style={{
                width: 32, height: 32, borderRadius: 8,
                backgroundColor: bg, display: 'flex', alignItems: 'center',
                justifyContent: 'center', color, fontSize: 13, flexShrink: 0, marginLeft: 8,
            }}>
                <FontAwesomeIcon icon={icon} />
            </div>
        </div>
        <div style={{ fontSize: 26, fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>{value ?? '—'}</div>
        {sub && <div style={{ fontSize: 11, color: '#9ca3af' }}>{sub}</div>}
    </div>
);

const SectionBlock = ({ title, accent, cards }) => (
    <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <div style={{ width: 3, height: 18, borderRadius: 2, backgroundColor: accent }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{title}</span>
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {cards.map((c) => <StatCard key={c.label} {...c} />)}
        </div>
    </div>
);

const fmt = (n) => (n != null ? Number(n).toLocaleString() : '—');
const fmtTons = (n) => (n != null ? `${parseFloat(n).toFixed(3)} T` : '—');

const MovementSummaryCards = ({ summary }) => {
    const { t } = useTranslation();
    if (!summary) return null;
    const totals = summary.totals ?? {};
    const top = summary.top_mover;
    const gap = summary.biggest_buy_sell_gap;

    const netInflow = (totals.net_weight_kg ?? 0) >= 0;

    const flowCards = [
        {
            label: t('movementAnalytics.cards.purchaseInbound'),
            value: fmtTons(totals.purchase_weight_tons),
            sub: `${fmt(totals.purchase_weight_kg)} kg`,
            icon: faArrowDown, color: '#059669', bg: '#ecfdf5', border: '#a7f3d0',
        },
        {
            label: t('movementAnalytics.cards.salesOutbound'),
            value: fmtTons(totals.sales_weight_tons),
            sub: `${fmt(totals.sales_weight_kg)} kg`,
            icon: faArrowUp, color: '#ef4444', bg: '#fef2f2', border: '#fecaca',
        },
        {
            label: netInflow ? t('movementAnalytics.cards.netInflow') : t('movementAnalytics.cards.netOutflow'),
            value: fmtTons(Math.abs(totals.net_weight_tons ?? 0)),
            sub: netInflow ? t('movementAnalytics.cards.boughtMoreByWeight') : t('movementAnalytics.cards.soldMoreByWeight'),
            icon: faScaleBalanced,
            color: netInflow ? '#059669' : '#d97706',
            bg: netInflow ? '#ecfdf5' : '#fffbeb',
            border: netInflow ? '#a7f3d0' : '#fde68a',
        },
        {
            label: t('movementAnalytics.cards.productsMoved'),
            value: fmt(totals.products_moved),
            sub: `${fmt(totals.products_purchased)} ${t('movementAnalytics.cards.boughtLabel')} · ${fmt(totals.products_sold)} ${t('movementAnalytics.cards.soldLabel')}`,
            icon: faArrowRightArrowLeft, color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc',
        },
    ];

    return (
        <div>
            <SectionBlock title={t('movementAnalytics.sections.flowOverview')} accent="#189AB4" cards={flowCards} />

            {(top || gap) && (
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
                    {top && (
                        <div style={highlightCard('#a7f3d0')}>
                            <div style={highlightHeader('#059669')}>
                                <FontAwesomeIcon icon={faStar} /> {t('movementAnalytics.sections.topMover')}
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: '#111827', marginBottom: 4 }}>{top.product_name}</div>
                            <div style={{ fontSize: 13, color: '#6b7280' }}>
                                {t('movementAnalytics.labels.busiestSku')}<span style={{ fontWeight: 700, color: '#059669' }}>{fmtTons(top.total_movement_tons)}</span> {t('movementAnalytics.labels.combinedInOut')}
                            </div>
                        </div>
                    )}
                    {gap && (
                        <div style={highlightCard('#fde68a')}>
                            <div style={highlightHeader('#d97706')}>
                                <FontAwesomeIcon icon={faScaleBalanced} /> {t('movementAnalytics.sections.biggestBuySellGap')}
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: '#111827', marginBottom: 4 }}>{gap.product_name}</div>
                            <div style={{ fontSize: 13, color: '#6b7280' }}>
                                {gap.direction === 'net_inflow' ? t('movementAnalytics.labels.netInflowText') : t('movementAnalytics.labels.netOutflowText')} ·{' '}
                                <span style={{ fontWeight: 700, color: '#d97706' }}>{fmtTons(gap.net_weight_tons)}</span>
                                {' '}({fmt(gap.purchase_weight_kg)} {t('movementAnalytics.labels.kgIn')} / {fmt(gap.sales_weight_kg)} {t('movementAnalytics.labels.kgOut')})
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const highlightCard = (border) => ({
    flex: '1 1 320px', minWidth: 300,
    backgroundColor: '#fff', border: `1px solid ${border}`,
    borderRadius: 14, padding: '16px 20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
});

const highlightHeader = (color) => ({
    fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase',
    letterSpacing: '0.5px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
});

export default MovementSummaryCards;
